import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/hooks/useLenis";

const links = [
  { key: "services", id: "catalog" },
  { key: "products", id: "catalog" },
  { key: "corporate", id: "about" },
  { key: "contact", id: "footer" },
];

export const Navbar = () => {
  const { t } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenu(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 pt-4"
      data-testid="navbar"
    >
      <div
        className={`mx-auto max-w-7xl rounded-2xl px-4 sm:px-6 h-16 flex items-center justify-between transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled ? "glass glass-gold" : "border border-transparent"
        }`}
      >
        <Logo />

        <nav className="hidden lg:flex items-center gap-9" aria-label="Main">
          {links.map((l) => (
            <button key={l.key} className="nav-link" onClick={() => go(l.id)} data-testid={`nav-${l.key}`}>
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <LanguageToggle />
          <button
            className="relative w-10 h-10 rounded-full border border-white/10 bg-white/[0.03] flex items-center justify-center text-[#e8eef0] transition-[border-color,box-shadow] duration-300 hover:border-[#00f2fe]/50 hover:shadow-[0_0_20px_rgba(0,242,254,0.25)]"
            onClick={() => setOpen(true)}
            data-testid="cart-button"
            aria-label="Cart"
          >
            <ShoppingBag size={18} strokeWidth={1.75} />
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  key="badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  className="cart-badge"
                  data-testid="cart-badge"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button
            className="lg:hidden w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#e8eef0]"
            onClick={() => setMenu((m) => !m)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
          >
            {menu ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden mx-auto max-w-7xl mt-2 glass glass-gold rounded-2xl p-4 flex flex-col"
            data-testid="mobile-menu"
          >
            {links.map((l) => (
              <button
                key={l.key}
                className="text-left py-3 px-2 font-display font-semibold text-lg text-[#e8eef0] border-b border-white/5 last:border-0"
                onClick={() => go(l.id)}
                data-testid={`mobile-nav-${l.key}`}
              >
                {t.nav[l.key]}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
